import {
    Badge,
    classNames,
    KeyButton,
    layout,
    PushButton,
    TextInput,
    theme,
    usePlatform,
    useTypeRef,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useRef, useState} from "react";
import {stylesheet} from "typestyle";
import {useSendBookingCode} from "./verify";
import {useLocalization} from "../../i18n";

interface BookingCodeSmsProps {
    onSent: (data: { phone: string }) => any;
    onCancel: () => any;
    code: string;
}

export function BookingCodeSms({onSent, onCancel, code}: BookingCodeSmsProps) {
    const input = useRef<HTMLInputElement | null>();
    const [type, setType] = useTypeRef();
    const {t} = useLocalization();
    const [phone, setPhone] = useState("");
    const platform = usePlatform();
    const send = useSendBookingCode(platform, code, "verifyPhoneUrl", onSent);
    const handleSubmit = useCallback(() => send.submit({phone}), [send.submit, phone]);

    const disabled = send.loading;

    return (
        <View className={classNames(layout.horizontal, layout.alignCenter, layout.center)}>
            <div className={classNames(layout.horizontal)}>
                <div className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.boxLeft)}>
                    <div>
                        <div className={styles.title}>
                            {t("BookingEnterPhoneNumber")}
                        </div>
                        <p>
                            {t("BookingEnterPhoneNumberDescription")}
                        </p>
                        <p>
                            {t("BookingPhoneNumberVerificationInfo")}
                        </p>
                        <p>
                            {send.invalid && (
                                <Badge error>
                                    {t("InvalidPhoneNumber")}
                                </Badge>
                            )}
                            {send.failed && (
                                <Badge error>
                                    {t("SendVerificationFailedInternal")}
                                </Badge>
                            )}
                        </p>
                    </div>
                    <div>
                        <PushButton onClick={onCancel}>{t("Back")}</PushButton>
                    </div>
                </div>
                <div className={classNames(theme.moreContrast)}>
                    <TextInput
                        type={"phone"}
                        onChange={setPhone}
                        disabled={disabled}
                        value={phone}
                        inputRef={input}
                        typeRef={setType}
                        className={styles.textInput}
                        placeholder={t("BookingPhoneNumberPlaceholder")}
                    />
                    <div className={styles.keyPad}>
                        <div className={layout.horizontal}>
                            <KeyButton value="1" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="2" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="3" border={false} onType={type} disabled={disabled}/>
                        </div>
                        <div className={layout.horizontal}>
                            <KeyButton value="4" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="5" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="6" border={false} onType={type} disabled={disabled}/>
                        </div>
                        <div className={layout.horizontal}>
                            <KeyButton value="7" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="8" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="9" border={false} onType={type} disabled={disabled}/>
                        </div>
                        <div className={layout.horizontal}>
                            <KeyButton value="+" border={false} onType={type} disabled={disabled}/>
                            <KeyButton value="0" border={false} onType={type} disabled={disabled}/>
                            <KeyButton
                                value={t("Submit")}
                                className={styles.submit}
                                onType={handleSubmit}
                                border={false}
                                primary
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </View>
    );
}

const styles = stylesheet({
    title: {
        fontSize: "1.5em",
    },
    boxLeft: {
        width: "20em",
        padding: "1.5em"
    },
    textInput: {
        fontWeight: "bold",
        width: "24rem",
        height: "4rem",
        fontSize: "1.75em"
    },
    keyPad: {
        fontSize: "1.75rem"
    },
    submit: {
        fontSize: "1.25rem"
    },
    button: {
        width: "100%",
    },
    marginBottom: {
        marginBottom: "1.5em",
    }
});